import { useState, useEffect } from "react";
import API from "../lib/api";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  ChartBar, TrendUp, Users, Clock, Funnel, CurrencyGbp,
  Rocket, Lightning, Crown, ShieldCheck, ArrowRight, CheckCircle
} from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts";

const PLAN_COLORS = {
  starter: "#3B82F6",
  professional: "#8B5CF6",
  enterprise: "#F59E0B",
  one_time_licence: "#10B981",
};

const PLAN_LABELS = {
  starter: "Starter",
  professional: "Professional",
  enterprise: "Enterprise",
  one_time_licence: "One-Time Licence",
};

const PLAN_ICONS = { starter: Rocket, professional: Lightning, enterprise: Crown, one_time_licence: ShieldCheck };

function KPICard({ icon: Icon, label, value, sublabel, accent }) {
  return (
    <div className="border border-border rounded-lg p-4 bg-card" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between mb-2">
        <Icon size={20} className={accent || "text-primary"} weight="duotone" />
      </div>
      <p className="text-2xl font-bold font-[Chivo] tracking-tight">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
      {sublabel && <p className="text-[10px] text-muted-foreground mt-0.5">{sublabel}</p>}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-popover border border-border rounded-md px-3 py-2 shadow-md text-xs">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          {p.name}: <span className="font-semibold">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

export default function TrialConversionPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/analytics/trial-conversions")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Failed to load conversion analytics"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-20 text-muted-foreground">Loading analytics...</div>;
  if (!data) return <div className="text-center py-20 text-muted-foreground">No data available</div>;

  const { summary, funnel, plan_distribution, monthly_trend, recent_conversions } = data;

  const pieData = plan_distribution.map(p => ({
    ...p,
    name: PLAN_LABELS[p.name] || p.name,
    fill: PLAN_COLORS[plan_distribution.find(x => (PLAN_LABELS[x.name] || x.name) === (PLAN_LABELS[p.name] || p.name))?.name] || "#6B7280",
  }));

  return (
    <div data-testid="trial-conversion-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center">
          <TrendUp size={18} className="text-white" weight="fill" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Trial Conversion Dashboard</h1>
          <p className="text-xs text-muted-foreground">Track trial-to-paid conversions and SaaS growth metrics</p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-6" data-testid="kpi-grid">
        <KPICard icon={Users} label="Total Trials" value={summary.total_trials} accent="text-blue-500" />
        <KPICard icon={Clock} label="Active Trials" value={summary.active_trials} sublabel="Currently trialing" accent="text-amber-500" />
        <KPICard icon={Funnel} label="Expired" value={summary.expired_trials} sublabel="Trial ended, not paid" accent="text-red-500" />
        <KPICard icon={CheckCircle} label="Converted" value={summary.converted} sublabel="Paid customers" accent="text-emerald-500" />
        <KPICard icon={TrendUp} label="Conversion Rate" value={`${summary.conversion_rate}%`} accent="text-violet-500" />
        <KPICard icon={Clock} label="Avg. Conversion" value={`${summary.avg_conversion_days}d`} sublabel="Days to convert" accent="text-teal-500" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">

        {/* Conversion Funnel */}
        <div className="border border-border rounded-lg p-4 bg-card" data-testid="conversion-funnel">
          <div className="flex items-center gap-2 mb-4">
            <Funnel size={16} className="text-muted-foreground" />
            <h3 className="text-sm font-semibold font-[Chivo]">Conversion Funnel</h3>
          </div>
          <div className="space-y-3">
            {funnel.map((stage, idx) => {
              const maxCount = funnel[0]?.count || 1;
              const pct = Math.round((stage.count / maxCount) * 100);
              const colors = ["bg-blue-500", "bg-amber-500", "bg-emerald-500"];
              return (
                <div key={stage.stage} data-testid={`funnel-stage-${idx}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">{stage.stage}</span>
                    <span className="text-xs font-semibold">{stage.count}</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-6 overflow-hidden">
                    <div
                      className={`h-full ${colors[idx] || "bg-primary"} rounded-full transition-all duration-700 flex items-center justify-end pr-2`}
                      style={{ width: `${Math.max(pct, 8)}%` }}
                    >
                      <span className="text-[10px] font-bold text-white">{pct}%</span>
                    </div>
                  </div>
                  {idx < funnel.length - 1 && (
                    <div className="flex justify-center py-1">
                      <ArrowRight size={12} className="text-muted-foreground rotate-90" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Monthly Trend */}
        <div className="border border-border rounded-lg p-4 bg-card" data-testid="monthly-trend-chart">
          <div className="flex items-center gap-2 mb-4">
            <ChartBar size={16} className="text-muted-foreground" />
            <h3 className="text-sm font-semibold font-[Chivo]">Monthly Trend</h3>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={monthly_trend} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} className="fill-muted-foreground" />
              <YAxis tick={{ fontSize: 10 }} className="fill-muted-foreground" allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="signups" name="Signups" fill="#3B82F6" radius={[3, 3, 0, 0]} />
              <Bar dataKey="conversions" name="Conversions" fill="#10B981" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Plan Distribution Pie */}
        <div className="border border-border rounded-lg p-4 bg-card" data-testid="plan-distribution-chart">
          <div className="flex items-center gap-2 mb-4">
            <CurrencyGbp size={16} className="text-muted-foreground" />
            <h3 className="text-sm font-semibold font-[Chivo]">Plan Distribution</h3>
            <span className="text-[10px] text-muted-foreground">(converted only)</span>
          </div>
          {plan_distribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={75}
                  paddingAngle={3}
                  dataKey="value"
                  label={({ name, value }) => `${name} (${value})`}
                  labelLine={false}
                >
                  {pieData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-xs text-muted-foreground text-center py-10">No conversions yet</p>
          )}
          {/* Legend */}
          <div className="flex flex-wrap gap-3 justify-center mt-2">
            {plan_distribution.map(p => (
              <div key={p.name} className="flex items-center gap-1.5 text-[10px]">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: PLAN_COLORS[p.name] || "#6B7280" }} />
                {PLAN_LABELS[p.name] || p.name}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Conversions Table */}
      <div className="border border-border rounded-lg overflow-hidden" data-testid="recent-conversions-table">
        <div className="px-4 py-3 bg-muted/30 border-b border-border">
          <h3 className="text-sm font-semibold font-[Chivo]">Recent Conversions</h3>
        </div>
        {recent_conversions.length === 0 ? (
          <p className="px-4 py-8 text-xs text-muted-foreground text-center">No conversions recorded yet</p>
        ) : (
          <div className="divide-y divide-border">
            {recent_conversions.map((c, i) => {
              const PlanIcon = PLAN_ICONS[c.plan] || Rocket;
              const signupDate = c.signup_at ? new Date(c.signup_at) : null;
              const convertDate = c.converted_at ? new Date(c.converted_at) : null;
              const daysDiff = signupDate && convertDate ? Math.round((convertDate - signupDate) / 86400000) : null;
              return (
                <div key={i} className="flex items-center justify-between px-4 py-3" data-testid={`conversion-row-${i}`}>
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-md flex items-center justify-center" style={{ background: `${PLAN_COLORS[c.plan] || "#6B7280"}18` }}>
                      <PlanIcon size={16} style={{ color: PLAN_COLORS[c.plan] || "#6B7280" }} weight="duotone" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{c.tenant_name}</p>
                      <p className="text-[10px] text-muted-foreground">
                        Signed up {signupDate?.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {daysDiff !== null && (
                      <span className="text-[10px] text-muted-foreground">{daysDiff}d to convert</span>
                    )}
                    <Badge
                      className="text-[9px]"
                      style={{ background: `${PLAN_COLORS[c.plan] || "#6B7280"}18`, color: PLAN_COLORS[c.plan] || "#6B7280" }}
                    >
                      {PLAN_LABELS[c.plan] || c.plan}
                    </Badge>
                    <span className="text-[10px] text-muted-foreground">
                      {convertDate?.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
